import { z } from "zod";

import { APPLICATION_ROLES, COGNITO_INTERNAL_STAFF_GROUPS, defineAbilityFor } from "@recobra/core";

const internalStaff = COGNITO_INTERNAL_STAFF_GROUPS;

/**
 * Values are stored verbatim on documents and referenced by task checklists
 * forever — add, never rename or remove.
 */
export const DocumentTypeSchema = z.enum([
  "claim_notice",
  "police_report",
  "accident_photos",
  "repair_estimate",
  "invoice",
  "drivers_license",
  "vehicle_document",
  "expert_report",
  "other",
  "post_repair_photos",
  "repair_work_order",
  "repair_invoice",
  "tow_invoice",
  "vehicle_purchase_invoice",
  "warranty_terms",
  "identity_document",
  "proof_of_address",
  "power_of_attorney",
  "bank_details",
  "income_proof",
  "payment_receipt",
  "settlement_agreement",
  "debt_confession",
  "insurance_policy",
  "vehicle_downtime_declaration",
  "notification_receipt_proof",
  "dvc",
  "third_party_contact_record",
  "extrajudicial_notice",
]);

export type DocumentType = z.infer<typeof DocumentTypeSchema>;

/** Types a task can ask for; the rest are produced during the case, not requested from anyone. */
export const REQUESTABLE_DOCUMENT_TYPES = DocumentTypeSchema.exclude([
  "settlement_agreement",
  "dvc",
  "third_party_contact_record",
  "extrajudicial_notice",
]).options;

export const REPAIR_PROOF_DOCUMENT_TYPES = [
  "repair_estimate",
  "repair_invoice",
  "repair_work_order",
] as const satisfies readonly DocumentType[];

const DocumentClassificationOverrideSchema = z.object({
  documentType: DocumentTypeSchema,
  overriddenBy: z.string().min(1),
  overriddenAt: z.iso.datetime(),
});

const DocumentStatusSchema = z.enum(["pending", "uploaded", "classifying", "classified", "failed"]);

export type DocumentStatus = z.infer<typeof DocumentStatusSchema>;

export const DocumentFailureReasonSchema = z.enum(["video_duration_exceeded"]);

export type DocumentFailureReason = z.infer<typeof DocumentFailureReasonSchema>;

export const MAX_VIDEO_ANALYSIS_DURATION_SECONDS = 10 * 60;

export const DocumentConfidenceLevelSchema = z
  .enum(["low", "medium", "high"])
  .describe(
    "Your subjective certainty that documentType is correct: low if unsure or the document is ambiguous/low quality, medium if reasonably confident, high if certain.",
  );

export const DocumentClassificationSchema = z.object({
  documentType: DocumentTypeSchema,
  confidence: DocumentConfidenceLevelSchema,
  classifiedAt: z.iso.datetime(),
  explanation: z.string().min(1).max(500).optional(),
  handwrittenTranscription: z
    .string()
    .min(1)
    .max(50_000)
    .optional()
    .describe(
      "Faithful transcription of handwritten content in the document. Omitted when the document contains no handwriting.",
    ),
});

export type DocumentClassification = z.infer<typeof DocumentClassificationSchema>;

export const DocumentVideoAnalysisSchema = z.object({
  summary: z.string().min(1).max(1_200),
  durationSeconds: z.number().positive().max(MAX_VIDEO_ANALYSIS_DURATION_SECONDS),
  analyzedAt: z.iso.datetime(),
});

export type DocumentVideoAnalysis = z.infer<typeof DocumentVideoAnalysisSchema>;

export const DocumentFileFingerprintSchema = z.string().regex(/^sha1:[a-f0-9]{40}$/);

export const DocumentSchema = z.object({
  id: z.uuid().meta({ visibleTo: "*" }),
  clientId: z.uuid().meta({ visibleTo: internalStaff }),
  branchId: z.uuid().nullish().meta({ visibleTo: internalStaff }),
  caseId: z.uuid().nullable().meta({ visibleTo: internalStaff }),
  documentType: z
    .string()
    .min(1)
    .nullable()
    .optional()
    .describe("Set by AI classification after upload; absent until classified.")
    .meta({ visibleTo: "*" }),
  s3Key: z.string().min(1).meta({ visibleTo: internalStaff }),
  filename: z.string().min(1).meta({ visibleTo: "*" }),
  mimeType: z.string().min(1).meta({ visibleTo: "*" }),
  sizeBytes: z.number().int().nonnegative().meta({ visibleTo: "*" }),
  fileFingerprint: DocumentFileFingerprintSchema.optional().meta({
    visibleTo: internalStaff,
  }),
  uploadedBy: z.string().min(1).meta({ visibleTo: internalStaff }),
  uploadSource: z
    .enum(["migration"])
    .optional()
    .describe(
      "Set on documents the Clientar data import stored; provenance only, never set on new uploads.",
    )
    .meta({ visibleTo: internalStaff }),
  status: DocumentStatusSchema.meta({ visibleTo: "*" }),
  failureReason: DocumentFailureReasonSchema.optional().meta({ visibleTo: "*" }),
  deletedAt: z.iso.datetime().nullable().meta({ visibleTo: internalStaff }),
  createdAt: z.iso.datetime().meta({ visibleTo: "*" }),
  updatedAt: z.iso.datetime().meta({ visibleTo: "*" }),
  classification: DocumentClassificationSchema.optional().meta({
    visibleTo: internalStaff,
  }),
  videoAnalysis: DocumentVideoAnalysisSchema.optional().meta({
    visibleTo: APPLICATION_ROLES.filter((group) =>
      defineAbilityFor({ groups: [group] }).can("readAnalysis", "Document"),
    ),
  }),
  classificationOverride: DocumentClassificationOverrideSchema.optional().meta({
    visibleTo: internalStaff,
  }),
  classificationOverrideHistory: z
    .array(DocumentClassificationOverrideSchema)
    .optional()
    .meta({ visibleTo: internalStaff }),
  notRelatedToCurrentCase: z.boolean().optional().meta({ visibleTo: internalStaff }),
  visibleTo: z
    .array(z.enum(APPLICATION_ROLES))
    .describe(
      "Roles allowed to read this document, derived from its type; a role absent here has no read access.",
    )
    .meta({ visibleTo: internalStaff }),
  viewUrl: z
    .url()
    .optional()
    .describe("Injected at read time — not stored in DB.")
    .meta({ visibleTo: "*" }),
  thumbnailUrl: z
    .url()
    .optional()
    .describe("Injected at read time — not stored in DB.")
    .meta({ visibleTo: "*" }),
});

export type DocumentRecord = z.infer<typeof DocumentSchema>;

export const OverrideClassificationRequestSchema = z.object({
  documentType: DocumentTypeSchema,
});
